import { API_BASE, AUTH_TOKEN } from '../config';

// The record shop counter — one turn with the clerk.
//
// STATELESS ON THE SERVER, so the history travels with the turn. Two people can
// be talking to him in two browsers without one of them getting the other's
// records, and a refresh is a new conversation rather than a corrupted one.
//
// He answers with a REPLY and, when he has pulled something, a SHELF: the full
// playable payload for the wall, already signed. `shelf: null` means he did not
// change the wall this turn — leave it alone rather than re-hanging identical
// sleeves, which would restart every video texture on it.
// `zone` says WHICH ROOM you are standing in, and it is how the server finds who
// is on shift: the placement in that zone carrying this job. Sent rather than
// inferred from the job's home zone, so a role worked in a room it was not
// written for still resolves. What is never sent is WHO — an avatar id accepted
// from a browser would let anybody point the clerk at any avatar on the platform
// and talk to it in that avatar's voice.
// `seat` (a café counter, 2026-10-30): { table, open } — where the customer is,
// so the barista knows whether a drink goes to their table or the bar.
export async function shopTalk({ actorKey, zone, message, history = [], seat = null }) {
  const res = await fetch(`${API_BASE}/api/world/shop/talk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Signed in or not, both work — the clerk talks to anybody. The token is
      // what makes "save that one" possible, and its absence is why he says he
      // cannot rather than failing.
      ...(AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
    },
    body: JSON.stringify({ actorKey, zone, message, history, ...(seat ? { seat } : {}) }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `the counter is busy (${res.status})`);
  return {
    reply: d.reply || '',
    shelf: d.shelf || null,
    liked: d.liked || [],
    // A drink the barista made (API CafeService take_order): { drink } | null.
    order: d.order || null,
    // What the cashier rang up (API GroceryService ring_up, 2026-10-30): { items } | null.
    purchase: d.purchase || null,
    // A can the instructor handed over (API GymService hand_fuel): { drink } | null.
    // ⚠ It was left out here, so a TYPED "can I get a GangstaFuel" never put the
    // can on the bar (applyRoleOut never saw it); only a spoken ask did.
    fuel: d.fuel || null,
    // The print shop's studio turned to a section (API PrintShopService show_studio):
    // { section, ...fill } | null.
    studio: d.studio || null,
    // His voice: a URL when the clip is ready, a poll key while it renders.
    // Absent entirely when the part has no voice set — then he stays text.
    audioUrl: d.audioUrl || null,
    audioKey: d.audioKey || null,
    // WHY he is silent, when he is. "No audio" has three causes that look
    // identical from inside the shop — no voice cast, an avatar with no voice of
    // its own, a synth that never started — and the server names which.
    voiced: d.voiced !== false,
    voiceReason: d.voiceReason || null,
  };
}

/** Save a record to this member's likes. The same endpoint the radio's heart uses. */
export async function likeTrack(trackId) {
  const res = await fetch(`${API_BASE}/api/radio/likes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
    body: JSON.stringify({ trackId }),
  });
  if (!res.ok) throw new Error(res.status === 401 ? 'sign in to save records' : `could not save (${res.status})`);
  return true;
}

export async function unlikeTrack(trackId) {
  const res = await fetch(`${API_BASE}/api/radio/likes/${trackId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
  });
  if (!res.ok) throw new Error(`could not remove (${res.status})`);
  return true;
}

/** Which of these are already saved — so a heart starts filled rather than lying. */
export async function likedIds() {
  try {
    if (!AUTH_TOKEN) return new Set();
    const res = await fetch(`${API_BASE}/api/radio/likes`, { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } });
    const d = await res.json().catch(() => ({}));
    const rows = d.likes || d.tracks || [];
    return new Set(rows.map((r) => Number(r.track_id)).filter(Boolean));
  } catch {
    return new Set();
  }
}
